import React, { useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

export default function MfaVerify() {
  const { verifyMfa } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const mfaSession = location.state?.mfa_session;
  const accountEmail = location.state?.email || "your account";
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  if (!mfaSession) {
    return <Navigate to="/login" replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setSubmitting(true);
    setError("");
    const res = await verifyMfa(mfaSession, code.trim());
    setSubmitting(false);
    if (!res.ok) {
      setError(res.error || "MFA verification failed");
      toast.error(res.error || "MFA verification failed");
      return;
    }
    toast.success(res.used_backup_code ? "Signed in with backup code" : "Welcome back");
    navigate("/cases", { replace: true });
  };

  return (
    <div
      data-testid="mfa-verify-page"
      className="relative flex min-h-screen items-center justify-center px-4"
      style={{ backgroundColor: "#0B0B12" }}
    >
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute left-1/2 top-1/2 h-[640px] w-[640px] -translate-x-1/2 -translate-y-1/2 dot-ring-glow opacity-80" />
      </div>
      <div className="relative z-10 w-full max-w-md">
        <div
          className="rounded-2xl border border-[color:var(--border-subtle)]/80 p-8 shadow-brand-lg"
          style={{ backgroundColor: "rgba(21, 21, 29, 0.9)", backdropFilter: "blur(8px)" }}
        >
          <h1 className="text-xl font-semibold tracking-tight text-white">Two-step verification</h1>
          <p className="mt-2 text-sm text-[color:var(--page-fg-secondary)]">
            Enter the 6-digit code from your authenticator app for{" "}
            <span className="font-semibold text-white">{accountEmail}</span>. You can also use a
            single-use backup code (8 hex characters).
          </p>
          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="mfa-verify-form">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-[color:var(--page-fg-secondary)]">
                Authenticator code or backup code
              </Label>
              <Input
                data-testid="mfa-code-input"
                inputMode="text"
                autoComplete="one-time-code"
                autoFocus
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="123 456  or  ABCD1234"
                className="h-11 rounded-xl border-[color:var(--border-subtle)] text-center font-mono text-lg tracking-widest text-white"
                style={{ backgroundColor: "#0F0F18" }}
              />
            </div>
            {error && (
              <p
                data-testid="mfa-error"
                className="rounded-lg px-3 py-2 text-sm"
                style={{ backgroundColor: "rgba(229, 72, 77, 0.16)", color: "#FCA5A8" }}
              >
                {error}
              </p>
            )}
            <Button
              type="submit"
              data-testid="mfa-submit-button"
              disabled={submitting}
              className="h-11 w-full rounded-xl font-semibold"
              style={{ backgroundColor: "#14B8B8", color: "#0B0B12" }}
            >
              {submitting ? "Verifying…" : "Verify and continue"}
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={() => navigate("/login", { replace: true })}
              className="h-9 w-full rounded-xl text-xs text-[color:var(--page-fg-secondary)] hover:text-white"
            >
              Cancel and use a different account
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
